// REST client for the envelope of 04 §2.3. Mutations get an Idempotency-Key (04 §2.5); an expired
// access token is refreshed once (single flight) and the request retried with the same key.
import type { ErrorCode } from '@stars-and-ships/api-types';
import { useSession } from '../state/session';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export interface RequestOptions {
  body?: unknown;
  /** Send the access token (default true). */
  auth?: boolean;
}

const API = '/api/v1';
const baseHeaders = (): Record<string, string> => ({ 'x-client-version': __APP_VERSION__ });

let refreshing: Promise<boolean> | null = null;

/** Exchanges the refresh cookie for a new access token. Resolves false when the session is over. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...baseHeaders(), 'content-type': 'application/json' },
        body: '{}',
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) return false;
      useSession.getState().setAccessToken(json.data.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function api<T>(
  method: string,
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const auth = options.auth ?? true;
  const mutation = method !== 'GET';
  const idempotencyKey = auth && mutation ? crypto.randomUUID() : null;

  const send = () => {
    const headers = baseHeaders();
    const token = useSession.getState().accessToken;
    if (auth && token) headers.authorization = `Bearer ${token}`;
    if (idempotencyKey) headers['idempotency-key'] = idempotencyKey;
    const init: RequestInit = { method, headers, credentials: 'include' };
    if (mutation) {
      headers['content-type'] = 'application/json';
      init.body = JSON.stringify(options.body ?? {});
    }
    return fetch(`${API}${path}`, init);
  };

  let res: Response;
  try {
    res = await send();
    if (res.status === 401 && auth) {
      const body = await res
        .clone()
        .json()
        .catch(() => null);
      if (body?.error?.code === 'ACCESS_TOKEN_EXPIRED' && (await refreshSession())) {
        res = await send();
      }
    }
  } catch {
    throw new ApiRequestError(0, 'NETWORK_ERROR', 'No connection to the server. Retrying helps.');
  }

  if (res.status === 204) return undefined as T;
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.success) {
    const error = json?.error;
    if (res.status === 401 && auth) useSession.getState().clear();
    throw new ApiRequestError(
      res.status,
      error?.code ?? 'INTERNAL_ERROR',
      error?.message ?? 'Something went wrong.',
      error?.details
    );
  }
  return json.data as T;
}
